import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import { useAuth } from "../provider/authProvider.js";
import * as global from "../data/global.js";
import { saveSelectedSite } from "../data/global.js";
import Device from "./DeviceModules.js";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    Spinner,
    SITE_WINDOW_IDS,
    ConfirmDialog,
    confirmDialogButtonSx,
    outlinedActionButtonSx,
    ensureSiteInfosLoaded,
} from "./windowChrome/index.js";

const getThemeColor = getWindowChromeThemeColor;

const DevicesWindow = () => {
    const { setToken, clearToken } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);
    const isFirstPassRef = useRef(true);
    const checkScanningRef = useRef(null);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));

    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedDeviceType, setSelectedDeviceType] = useState(null);
    const [modules, setModules] = useState([]);
    const [openWrite, setOpenWrite] = useState(false);
    const [openRead, setOpenRead] = useState(false);
    const [isScanning, setIsScanning] = useState(false);

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    const fieldSx = useMemo(() => ({
        width: "100%",
        minWidth: 0,
        maxWidth: { xs: "none", sm: 220 },
        "& .MuiOutlinedInput-root": {
            color: getThemeColor("#333333", "#f4cc8e"),
            backgroundColor: getThemeColor("#ffffff", "#2d2d2d"),
            fontSize: "14px",
            "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
            "&:hover fieldset": { borderColor: getThemeColor("#666666", "#f4cc8e") },
            "&.Mui-focused fieldset": { borderColor: getThemeColor("#666666", "#f4cc8e") },
        },
        "& .MuiInputLabel-root": { color: getThemeColor("#666666", "#a8abb1"), fontSize: "14px" },
        "& .MuiInputLabel-root.Mui-focused": { color: getThemeColor("#333333", "#f4cc8e") },
        "& .MuiInputBase-input": {
            color: getThemeColor("#333333", "#f4cc8e"),
            fontSize: "14px",
            paddingTop: "8.5px",
            paddingBottom: "8.5px",
        },
        "& .MuiSvgIcon-root": { color: getThemeColor("#666666", "#a8abb1") },
    }), [forceUpdate]);

    const actionBtnSx = useMemo(() => outlinedActionButtonSx(), [forceUpdate]);

    useEffect(() => {
        const onTheme = () => setForceUpdate((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apply = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            if (cancelled) return;
            setSites(all);
            setSitesLoading(false);
        };
        if ((global.gw.siteInfos || []).length > 0) {
            apply();
            return undefined;
        }
        setSitesLoading(true);
        ensureSiteInfosLoaded().then(() => {
            if (!cancelled) apply();
        });
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const clearRefreshTimer = useCallback(() => {
        if (global.gw.selectedSite?.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
            global.gw.selectedSite.refreshId = null;
        }
    }, []);

    const redirectToLogin = useCallback(() => {
        clearRefreshTimer();
        clearToken();
        delete axios.defaults.headers.common.Authorization;
        global.gw.token = null;
        global.gw.auth = null;
        global.gw.isAuthenticated = false;
        setTimeout(() => {
            window.location.href = "/emailLogin";
        }, 100);
    }, [clearRefreshTimer, clearToken]);

    const checkIfScanning = useCallback(() => {
        if (isFirstPassRef.current) {
            clearRefreshTimer();
            global.gw.selectedSite.refreshId = setTimeout(() => {
                checkScanningRef.current?.();
            }, global.gw.selectedSite.refreshRate);
            isFirstPassRef.current = false;
            return;
        }

        if (!global.gw.selectedSite?.siteGuid || !global.gw.token) return;

        axios({
            url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
            method: "post",
            timeout: 10000,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${global.gw.token}`,
            },
            data: {
                query: `
                    query UserScan($email: String!, $siteId: ID!) {
                        getLastScan(email: $email, siteId: $siteId) {
                            id,
                            dateStarted,
                            scanCable,
                            finishedCables,
                            isComplete,
                            isCanceled,
                            jsonCommand
                        }
                    }
                `,
                variables: {
                    email: global.gw.email,
                    siteId: global.gw.selectedSite.siteGuid,
                },
            },
        }).then((response) => {
            const lastScan = response?.data?.data?.getLastScan;
            if (!lastScan) {
                setIsScanning(false);
            } else {
                const dateRead = new Date(lastScan.dateStarted.replace("Z", "-05:00"))
                    .toLocaleString("en-us", global.dateFormat)
                    .replaceAll("/", "-")
                    .replace(",", "");
                if (
                    !lastScan.isCanceled
                    && (
                        (global.gw.selectedSite.selectedMark && dateRead !== global.gw.selectedSite.selectedMark.label)
                        || !lastScan.isComplete
                    )
                ) {
                    setIsScanning(true);
                } else if (
                    lastScan.isComplete
                    && global.gw.selectedSite.finishedCables.length > 0
                    && lastScan.finishedCables
                    && lastScan.finishedCables !== ""
                    && JSON.parse(lastScan.jsonCommand).length !== global.gw.selectedSite.finishedCables.length
                ) {
                    setIsScanning(false);
                } else {
                    setIsScanning(false);
                }
            }

            clearRefreshTimer();
            global.gw.selectedSite.refreshId = setTimeout(() => {
                checkScanningRef.current?.();
            }, global.gw.selectedSite.refreshRate);
        }).catch((error) => {
            const is401 = (error.response && error.response.status === 401)
                || (error.message && (error.message.includes("401") || error.message.includes("Unauthorized")));
            if (is401) {
                redirectToLogin();
                return;
            }

            clearRefreshTimer();
            if (global.gw.selectedSite) {
                global.gw.selectedSite.refreshId = setTimeout(() => {
                    checkScanningRef.current?.();
                }, global.gw.selectedSite.refreshRate);
            }

            if (
                error.message
                && error.message.endsWith("401")
                && global.gw.token
                && global.gw.auth
            ) {
                createApiEndpoint(global.Endpoints.GrainWave)
                    .fetchRenew()
                    .then((r) => {
                        if (r && global.gw.token) setToken(global.gw.token);
                        else clearToken();
                    })
                    .catch(() => clearToken());
            }
        });
    }, [clearRefreshTimer, clearToken, redirectToLogin, setToken]);

    checkScanningRef.current = checkIfScanning;

    const applyDevicesFromSite = useCallback(() => {
        const nextDevices = global.gw.selectedSite.devices || [];
        const nextModules = global.gw.selectedSite.modules || [];
        setDevices(nextDevices);
        setModules(nextModules);
        if (nextDevices.length > 0) {
            setSelectedDevice(nextDevices[0].ipAddress);
            setSelectedDeviceType(nextDevices[0].type);
        } else {
            setSelectedDevice(null);
            setSelectedDeviceType(null);
        }
    }, []);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        clearRefreshTimer();
        isFirstPassRef.current = true;
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                saveSelectedSite?.();
            } else if (global.gw.selectedSite) {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);

            let retryCount = 0;
            const maxRetries = 30;
            while (retryCount < maxRetries) {
                const nextDevices = global.gw.selectedSite.devices || [];
                const nextModules = global.gw.selectedSite.modules || [];
                if (nextDevices.length > 0 || nextModules.length > 0) {
                    break;
                }
                await new Promise((resolve) => setTimeout(resolve, 100));
                retryCount++;
            }

            applyDevicesFromSite();
            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setPhase("view");
            checkIfScanning();
        } catch (error) {
            console.error("DevicesWindow: failed to load site", error);
            const is401 = (error.response && error.response.status === 401)
                || (error.message && (error.message.includes("401") || error.message.includes("Unauthorized")));
            if (is401 && global.gw.token && global.gw.auth) {
                redirectToLogin();
                return;
            }
            setPhase("select");
            setLoadedSiteGuid("");
            setDevices([]);
            setModules([]);
            setSelectedDevice(null);
            setSelectedDeviceType(null);
        } finally {
            setLoading(false);
        }
    }, [applyDevicesFromSite, checkIfScanning, clearRefreshTimer, navigate, redirectToLogin]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    useEffect(() => () => {
        clearRefreshTimer();
    }, [clearRefreshTimer]);

    const handleContinue = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    }, [pickerSiteGuid, applySiteSelection]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [applySiteSelection]);

    const handleClose = () => {
        clearRefreshTimer();
        navigate("/sites", { replace: true });
    };

    const handleDeviceChange = (e) => {
        const ip = e.target.value;
        setSelectedDevice(ip);
        const device = devices.find((d) => d.ipAddress === ip);
        setSelectedDeviceType(device?.type ?? null);
    };

    const handleModuleChange = (moduleId, rKey, checked) => {
        setModules((prevModules) =>
            prevModules.map((module) =>
                module.id === moduleId
                    ? { ...module, [rKey]: checked ? 1 : 0 }
                    : module,
            ),
        );
    };

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                className="devicesWindow"
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={handleClose}
                    leading={
                        phase === "view" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Devices"
                                siteWindowId={SITE_WINDOW_IDS.devices}
                                siteGuid={activeSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={activeSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                sites={sites}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={loading || sitesLoading}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Devices"
                            siteWindowId={SITE_WINDOW_IDS.devices}
                            siteGuid={activeSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                    actions={
                        phase === "view" && !loading ? (
                            <>
                                <MuiMaterials.TextField
                                    select
                                    size="small"
                                    label="Device"
                                    value={selectedDevice || ""}
                                    onChange={handleDeviceChange}
                                    disabled={!devices.length}
                                    sx={fieldSx}
                                >
                                    {devices.length === 0 ? (
                                        <MuiMaterials.MenuItem value="" disabled>
                                            No Devices
                                        </MuiMaterials.MenuItem>
                                    ) : (
                                        devices.map((device) => (
                                            <MuiMaterials.MenuItem
                                                key={device.ipAddress}
                                                value={device.ipAddress}
                                            >
                                                {device.ipAddress}
                                            </MuiMaterials.MenuItem>
                                        ))
                                    )}
                                </MuiMaterials.TextField>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={actionBtnSx}
                                    onClick={() => setOpenWrite(true)}
                                >
                                    Write
                                </MuiMaterials.Button>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={actionBtnSx}
                                    onClick={() => setOpenRead(true)}
                                >
                                    Read
                                </MuiMaterials.Button>
                            </>
                        ) : null
                    }
                />

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinue}
                        onCancel={handleClose}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                    />
                ) : null}

                {phase === "view" && !loading ? (
                    <ModalBody className="hide-scrollbar">
                        <ModalContentTile
                            className="hide-scrollbar"
                            sx={{
                                overflowY: "auto",
                                overflowX: "auto",
                                p: "12px 16px",
                            }}
                        >
                            {devices.length === 0 ? (
                                <MuiMaterials.Typography
                                    sx={{
                                        color: textSecondary,
                                        fontSize: 14,
                                        py: 2,
                                    }}
                                >
                                    No devices are available for this site.
                                </MuiMaterials.Typography>
                            ) : (
                                <Device
                                    windowStyle={{
                                        maxHeight: "100%",
                                        zIndex: "100",
                                        width: "100%",
                                    }}
                                    selectedDevice={selectedDevice}
                                    selectedDeviceType={selectedDeviceType}
                                    modules={modules}
                                    onModuleChange={handleModuleChange}
                                    forceUpdate={forceUpdate}
                                />
                            )}
                        </ModalContentTile>
                    </ModalBody>
                ) : null}

                <ConfirmDialog
                    open={openWrite}
                    onClose={() => setOpenWrite(false)}
                    titleId="devices-write-title"
                    title={
                        isScanning
                            ? "A scan is in progress so this will not work."
                            : "This will write to all outputs."
                    }
                    actions={(
                        <>
                            <MuiMaterials.Button
                                size="small"
                                variant="outlined"
                                sx={confirmDialogButtonSx()}
                                onClick={() => setOpenWrite(false)}
                            >
                                Cancel
                            </MuiMaterials.Button>
                            {!isScanning ? (
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={confirmDialogButtonSx()}
                                    onClick={() => setOpenWrite(false)}
                                >
                                    Write
                                </MuiMaterials.Button>
                            ) : null}
                        </>
                    )}
                />

                <ConfirmDialog
                    open={openRead}
                    onClose={() => setOpenRead(false)}
                    titleId="devices-read-title"
                    title="This will read all inputs and outputs."
                    actions={(
                        <>
                            <MuiMaterials.Button
                                size="small"
                                variant="outlined"
                                sx={confirmDialogButtonSx()}
                                onClick={() => setOpenRead(false)}
                            >
                                Cancel
                            </MuiMaterials.Button>
                            <MuiMaterials.Button
                                size="small"
                                variant="outlined"
                                sx={confirmDialogButtonSx()}
                                onClick={() => setOpenRead(false)}
                            >
                                Read
                            </MuiMaterials.Button>
                        </>
                    )}
                />
            </div>
        </MuiMaterials.ThemeProvider>
    );
};

export default DevicesWindow;
